import { useEffect, useState } from "react";
import { Mail } from "lucide-react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate, money } from "@/lib/money";
import { INQ_STATUS } from "@/components/admin/inquiryStatus";
import { TeamCard } from "@/components/admin/TeamCard";

const card = "rounded-2xl border border-[#2E2A24] bg-[#121215] p-5";
const kicker = "font-mono-rc text-[11px] uppercase tracking-[0.2em] text-zinc-400";
const dayLabel = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });
const CAL = { booked: "bg-[#F5EFE6] text-[#0A0A0C]", blocked: "bg-zinc-600 text-white", pending: "bg-[#C4832B] text-[#0A0A0C]" };

const Metric = ({ label, value, hint, testId }) => (
  <div className={card} data-testid={testId}>
    <p className={kicker}>{label}</p>
    <p className="mt-2 font-display text-3xl text-[#F5EFE6] sm:text-4xl">{value}</p>
    {hint && <p className="mt-1 text-xs text-zinc-400">{hint}</p>}
  </div>
);

const TrafficChart = ({ traffic, days, setDays }) => (
  <div className={`${card} lg:col-span-2`} data-testid="overview-traffic">
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div>
        <p className={kicker}>Website traffic</p>
        <p className="mt-1 text-sm text-zinc-300">{traffic.views} page views · {traffic.visitors} visitors · {traffic.all_time_views} views all time</p>
      </div>
      <div className="flex gap-1 rounded-full border border-[#2E2A24] p-1">
        {[7, 30, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)} className={`rounded-full px-3 py-1 text-xs transition-[background-color] ${days === d ? "bg-[#C4832B] text-[#0A0A0C]" : "text-zinc-300 hover:bg-[#1A1A20]"}`} data-testid={`traffic-range-${d}`}>{d} days</button>
        ))}
      </div>
    </div>
    <div className="h-56">
      <ResponsiveContainer width="100%" height={224} minWidth={0}>
        <AreaChart data={traffic.series} margin={{ left: -20, right: 8, top: 8 }}>
          <CartesianGrid stroke="#2E2A24" vertical={false} />
          <XAxis dataKey="day" tickFormatter={dayLabel} tick={{ fill: "#a1a1aa", fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={24} />
          <YAxis allowDecimals={false} tick={{ fill: "#a1a1aa", fontSize: 11 }} axisLine={false} tickLine={false} />
          <Tooltip labelFormatter={dayLabel} contentStyle={{ background: "#0A0A0C", border: "1px solid #2E2A24", borderRadius: 12, color: "#F5EFE6" }} />
          <Area type="monotone" dataKey="views" name="Page views" stroke="#C4832B" fill="#C4832B" fillOpacity={0.18} strokeWidth={2} />
          <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#F5EFE6" fill="transparent" strokeWidth={1.5} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const List = ({ title, rows, empty, testId }) => (
  <div className={card} data-testid={testId}>
    <p className={`${kicker} mb-3`}>{title}</p>
    {rows.length ? <ul className="space-y-2">{rows}</ul> : <p className="text-sm text-zinc-400">{empty}</p>}
  </div>
);

const Sources = ({ traffic }) => (
  <List title="Top pages & sources" testId="overview-sources" empty="No visits yet."
    rows={[
      ...traffic.top_pages.map((p) => <li key={`p${p.path}`} className="flex justify-between text-sm"><span className="truncate text-zinc-300">{p.path}</span><span className="text-[#F5EFE6]">{p.views}</span></li>),
      ...traffic.top_referrers.map((r) => <li key={`r${r.source}`} className="flex justify-between text-sm"><span className="truncate text-zinc-400">from {r.source}</span><span className="text-[#F5EFE6]">{r.views}</span></li>),
    ]} />
);

const Contacts = ({ inquiries, onOpenTab }) => (
  <div className={card} data-testid="overview-contacts">
    <div className="mb-3 flex items-center justify-between">
      <p className={kicker}>Contacts</p>
      <button onClick={() => onOpenTab("inquiries")} className="text-xs text-[#E09838] hover:underline" data-testid="overview-open-inquiries">View all</button>
    </div>
    <div className="mb-4 flex flex-wrap gap-2">
      {INQ_STATUS.map((s) => <span key={s.v} className={`rounded-full px-3 py-1 text-xs ${s.cls}`}>{s.label} {inquiries.by_status?.[s.v] ?? 0}</span>)}
    </div>
    {inquiries.recent.length ? (
      <ul className="space-y-2">
        {inquiries.recent.map((i) => (
          <li key={i.id} className="flex justify-between gap-3 text-sm">
            <span className="min-w-0"><span className="block truncate text-[#F5EFE6]">{i.full_name}</span><span className="block truncate text-xs text-zinc-400">{i.service}</span></span>
            <span className="shrink-0 text-xs text-zinc-400">{fmtDate(i.created_at)}</span>
          </li>
        ))}
      </ul>
    ) : <p className="text-sm text-zinc-400">No inquiries yet.</p>}
  </div>
);

const Upcoming = ({ calendar, onOpenTab }) => (
  <div className={card} data-testid="overview-calendar">
    <div className="mb-3 flex items-center justify-between gap-3">
      <p className={kicker}>Calendar · {calendar.booked} booked · {calendar.pending} pending</p>
      <button onClick={() => onOpenTab("calendar")} className="text-xs text-[#E09838] hover:underline" data-testid="overview-open-calendar">Open</button>
    </div>
    {calendar.upcoming.length ? (
      <ul className="space-y-2">
        {calendar.upcoming.map((e) => (
          <li key={`${e.date}${e.label}`} className="flex items-center gap-3 text-sm">
            <span className="w-16 shrink-0 text-zinc-300">{dayLabel(e.date)}</span>
            <span className="min-w-0 flex-1 truncate text-zinc-400">{e.label || "—"}</span>
            <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] ${CAL[e.status]}`}>{e.status === "blocked" ? "Blacked out" : e.status === "pending" ? "Pending" : "Booked"}</span>
          </li>
        ))}
      </ul>
    ) : <p className="text-sm text-zinc-400">Nothing on the calendar yet.</p>}
  </div>
);

const PaymentsDue = ({ money: m, onOpenTab }) => (
  <div className={card} data-testid="overview-payments">
    <div className="mb-3 flex items-center justify-between">
      <p className={kicker}>Payments due</p>
      <button onClick={() => onOpenTab("invoices")} className="text-xs text-[#E09838] hover:underline" data-testid="overview-open-invoices">Invoices</button>
    </div>
    {m.due.length ? (
      <ul className="space-y-2">
        {m.due.map((d) => (
          <li key={d.id} className="flex justify-between gap-3 text-sm">
            <span className="min-w-0"><span className="block truncate text-[#F5EFE6]">{d.client_name}</span><span className="block truncate text-xs text-zinc-400">{d.number} · {d.due_date ? `due ${fmtDate(`${d.due_date}T12:00:00`)}` : "no due date"}</span></span>
            <span className={`shrink-0 text-right ${d.overdue ? "text-red-300" : "text-[#F5EFE6]"}`}>{money(d.deposit_due || d.balance_due)}<span className="block text-[11px] text-zinc-400">{d.overdue ? "overdue" : d.deposit_due ? "deposit" : "balance"}</span></span>
          </li>
        ))}
      </ul>
    ) : <p className="text-sm text-zinc-400">Nothing outstanding.</p>}
  </div>
);

const SnapshotButton = () => {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/snapshot/send");
      toast.success(`${data.month} snapshot sent to ${data.to}`);
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <div className={`${card} flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between`} data-testid="snapshot-card">
      <div>
        <p className={kicker}>Monthly snapshot email</p>
        <p className="mt-1 text-sm text-zinc-300">Arrives on the 1st of every month with last month's visits, inquiries, bookings and payments.</p>
      </div>
      <button onClick={send} disabled={busy} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full border border-[#C4832B] px-4 py-2 text-sm text-[#E09838] transition-[background-color] hover:bg-[#C4832B]/10 disabled:opacity-50" data-testid="snapshot-send-btn">
        <Mail className="h-4 w-4" /> {busy ? "Sending…" : "Email last month's now"}
      </button>
    </div>
  );
};

export const OverviewPanel = ({ onOpenTab }) => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/admin/overview?days=${days}`).then((r) => alive && setData(r.data)).catch((e) => toast.error(formatApiError(e)));
    return () => { alive = false; };
  }, [days]);

  if (!data) return <p className="py-10 text-center font-mono-rc text-xs tracking-[0.3em] text-[#C4832B]" data-testid="overview-loading">LOADING</p>;
  const { traffic, inquiries, contracts, money: m, calendar } = data;

  return (
    <div className="space-y-4" data-testid="overview-panel">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Metric label="Views today" value={traffic.today_views} hint={`${traffic.views} in ${days} days`} testId="metric-views" />
        <Metric label="New inquiries" value={inquiries.by_status?.new ?? 0} hint={`${inquiries.total} total`} testId="metric-new-inquiries" />
        <Metric label="Awaiting signature" value={contracts.awaiting_signature} hint={`${contracts.signed} signed`} testId="metric-contracts" />
        <Metric label="Outstanding" value={money(m.outstanding)} hint={m.overdue ? `${money(m.overdue)} overdue` : `${money(m.collected)} collected`} testId="metric-outstanding" />
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <TrafficChart traffic={traffic} days={days} setDays={setDays} />
        <Sources traffic={traffic} />
        <Contacts inquiries={inquiries} onOpenTab={onOpenTab} />
        <Upcoming calendar={calendar} onOpenTab={onOpenTab} />
        <PaymentsDue money={m} onOpenTab={onOpenTab} />
      </div>
      <SnapshotButton />
      <TeamCard />
    </div>
  );
};
